import React, { useEffect, useState } from "react";
import { Truck, Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { plate_number: "", vehicle_type: "sedan", status: "available", insurance_expiry: "", last_maintenance_at: "" };

export default function FleetVehicles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirm, setConfirm] = useState({ open: false });

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/fleet/vehicles");
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.plate_number) { toast.error("Plate number required"); return; }
    try {
      if (editing?.id) await api.put(`/fleet/vehicles/${editing.id}`, form);
      else await api.post("/fleet/vehicles", form);
      toast.success("Vehicle saved");
      setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(v) {
    try { await api.delete(`/fleet/vehicles/${v.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="fleet-vehicles-page">
      <PageHeader title="Vehicle Management" subtitle="Fleet vehicle registry — admin-managed." actions={
        <Button onClick={() => { setEditing({ id: null }); setForm(EMPTY); }} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="fleet-vehicle-new">
          <Plus className="w-4 h-4 mr-1.5" /> New Vehicle
        </Button>
      } />
      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <div className="hah-card p-2">
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left p-2.5">Plate</th>
                <th className="text-left p-2.5">Type</th>
                <th className="text-left p-2.5">Status</th>
                <th className="text-left p-2.5">Insurance</th>
                <th className="text-left p-2.5">Last Maintenance</th>
                <th className="text-right p-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr key={v.id} className="border-t border-[#E5E7EB]" data-testid={`vehicle-row-${v.plate_number}`}>
                  <td className="p-2.5 font-mono text-[#1F2937]">{v.plate_number}</td>
                  <td className="p-2.5 capitalize">{v.vehicle_type}</td>
                  <td className="p-2.5 capitalize"><span className="px-2 py-0.5 rounded bg-[#F5F7FA] text-[10px] uppercase font-bold">{v.status.replace("_", " ")}</span></td>
                  <td className="p-2.5 text-[#6B7280]">{v.insurance_expiry || "—"}</td>
                  <td className="p-2.5 text-[#6B7280]">{v.last_maintenance_at || "—"}</td>
                  <td className="p-2.5 text-right">
                    <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB] mr-1.5"
                      onClick={() => { setEditing(v); setForm({ plate_number: v.plate_number, vehicle_type: v.vehicle_type, status: v.status, insurance_expiry: v.insurance_expiry || "", last_maintenance_at: v.last_maintenance_at || "" }); }}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 border-[#EF444433] text-[#EF4444]"
                      onClick={() => setConfirm({ open: true, title: "Delete vehicle", description: `Delete ${v.plate_number}?`, destructive: true, onConfirm: () => remove(v) })}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Vehicle" : "New Vehicle"}</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <FormField label="Plate number" required>
              <Input value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value })} disabled={!!editing?.id} data-testid="vehicle-plate" />
            </FormField>
            <FormField label="Type">
              <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                <SelectTrigger data-testid="vehicle-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["sedan", "suv", "van", "ambulance", "motorbike"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="vehicle-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["available", "assigned", "en_route", "maintenance", "out_of_service"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Insurance expiry (YYYY-MM-DD)">
              <Input value={form.insurance_expiry} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} placeholder="2026-12-31" />
            </FormField>
            <FormField label="Last maintenance (YYYY-MM-DD)">
              <Input value={form.last_maintenance_at} onChange={(e) => setForm({ ...form, last_maintenance_at: e.target.value })} placeholder="2026-01-15" />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={save} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="vehicle-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirm.open} onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title} description={confirm.description} destructive={confirm.destructive}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }} />
    </div>
  );
}
